import {AnswerStatus, QuestionDifficulty} from '@domain/pom'
import React, {useCallback, useMemo} from 'react'

import {CompletionCard} from '../components'
import {useTopicActions, useStringSearchParameter, useQuestionState} from '../hooks'
import {BackendQuestion, Topic} from '../types'
import {countAllQuestionsSubmissions} from '../utils'

import {QuestionListSideCardContainer} from './QuestionListSideCardContainer'
import {SectionTopContainer} from './SectionTopContainer'
import {TopicSideCardContainer} from './TopicSideCardContainer'

interface TopicContainerProps {
  topic: Topic
}

export const TopicContainer: React.FC<TopicContainerProps> = ({topic}) => {
  const {data: actions, status} = useTopicActions(topic.id)
  const [, setQuestionId] = useStringSearchParameter('selectedQuestionId')

  const [{activeAnswerStatus, activeDifficulty}, {setActiveDifficulty, setActiveAnswerStatus}] =
    useQuestionState()

  const onScoreSectionClick = useCallback(
    (status?: AnswerStatus) => {
      setActiveAnswerStatus(status)
      setQuestionId(undefined)
    },
    [setActiveAnswerStatus, setQuestionId]
  )
  const onDifficultySectionClick = useCallback(
    (difficulty: QuestionDifficulty) => {
      setActiveDifficulty(difficulty)
      setQuestionId(undefined)
    },
    [setActiveDifficulty, setQuestionId]
  )

  const submissionSummary = useMemo(() => {
    if (!topic?.questions) return undefined
    return countAllQuestionsSubmissions(topic.questions, activeDifficulty)
  }, [activeDifficulty, topic])

  const filterQuestions = useCallback(({id}: BackendQuestion) => id.includes(topic.id), [topic.id])

  return (
    <SectionTopContainer
      actionLoadingStatus={status}
      isLoading={!actions}
      sideCardComponent={
        activeAnswerStatus && submissionSummary ? (
          <QuestionListSideCardContainer
            difficulty={submissionSummary.difficulty}
            customQuestionsFilterFunction={filterQuestions}
          />
        ) : (
          <TopicSideCardContainer actions={actions || []} />
        )
      }
    >
      <CompletionCard
        activeDifficulty={activeDifficulty}
        onScoreSectionClick={onScoreSectionClick}
        onDifficultySectionClick={onDifficultySectionClick}
        responsiblePerson={topic.responsible}
        submissionSummary={submissionSummary}
        difficultySummaries={topic.difficultySummaries}
      />
    </SectionTopContainer>
  )
}
